/**
 * `pending-applitools-visual-ai` — admin-config slice for the
 * smart-region matching pass (V2/V3).
 *
 * Off by default. When enabled, the diff worker runs per-region
 * SSIM on top of the existing whole-frame pixel diff. The
 * `ssimThreshold` is the cut-off below which a region counts as
 * "failed"; the default 0.97 splits the difference between
 * Applitools (0.95) and Percy (0.99).
 */

import { z } from 'zod';

/**
 * `pending-applitools-visual-ai` v1.2 — cross-browser matrix slice.
 *
 * Sub-slice of the parent `visualAi` admin-config row that flips
 * the Ultrafast Grid-style fan-out on. Off by default; when on,
 * `<VisualBaselineReview>` exposes a "Cross-browser matrix"
 * affordance that fans the same flow across every cell in
 * `defaultCells` and renders the per-cell SSIM result against the
 * anchor cell.
 *
 * The cell shape here is intentionally minimal — it mirrors the
 * `ExecutionCapability` tuple `run-capabilities.ts` defines, but
 * carries only the fields the matrix actually needs (the resolver
 * fills in the rest). Operators define a friendly `label` per
 * cell so the grid UI doesn't render an opaque tuple as a column
 * header.
 *
 * The cells stay loose here (free-form strings) to avoid coupling
 * `visualAi` to the full `run-capabilities` enum surface. The
 * matrix orchestrator validates the tuple against
 * `ExecutionCapabilitySchema` at run-submit time; here we only
 * pin the shape, not the values.
 */
export const VisualMatrixAdminCellSchema = z
  .object({
    label: z.string().min(1).max(64),
    engine: z.string().min(1).max(32),
    browser: z.string().min(1).max(32),
    os: z.string().min(1).max(32),
    osVersion: z.string().min(1).max(32).optional(),
    browserVersion: z.string().min(1).max(32).optional(),
    device: z.string().min(1).max(64).optional(),
    viewport: z
      .object({
        width: z.number().int().positive(),
        height: z.number().int().positive(),
      })
      .optional(),
  })
  .strict();
export type VisualMatrixAdminCell = z.infer<typeof VisualMatrixAdminCellSchema>;

export const VisualMatrixConfigSchema = z
  .object({
    enabled: z.boolean().default(false),
    /**
     * Default cells offered to the operator when they click
     * "Compare cross-browser". Empty array → the UI prompts the
     * operator to pick at run-submit time; non-empty → the cells
     * pre-populate.
     */
    defaultCells: z.array(VisualMatrixAdminCellSchema).max(20).default([]),
    /**
     * Anchor cell label. Resolved by exact-match against
     * `defaultCells[].label`. When unset or no match, the first
     * cell in `defaultCells` becomes the anchor.
     */
    anchorCellLabel: z.string().min(1).max(64).optional(),
    /**
     * Per-org maximum cells per matrix run. Default 8 mirrors
     * Applitools Ultrafast Grid's free-tier ceiling; cap is 20
     * to mirror `parallel-runs.capabilities.max`.
     */
    maxCells: z.number().int().min(2).max(20).default(8),
  })
  .strict();
export type VisualMatrixConfig = z.infer<typeof VisualMatrixConfigSchema>;

export const VisualAiConfigSchema = z
  .object({
    enabled: z.boolean().default(false),
    ssimThreshold: z.number().min(0).max(1).default(0.97),
    windowSize: z.number().int().min(4).max(64).default(8),
    /**
     * Default ignore-region presets the painter UI offers as
     * one-click chips. Operators can still draw custom regions
     * per-baseline; this list is the starter palette.
     */
    dynamicMaskingPresets: z
      .array(
        z.object({
          name: z.string(),
          selector: z.string(),
        }),
      )
      .default([
        { name: 'Timestamps', selector: '.timestamp, time, [data-time]' },
        { name: 'Carousels', selector: '.carousel, [data-carousel]' },
        { name: 'Live counters', selector: '.counter[data-live]' },
      ]),
    /**
     * v1.2 — cross-browser visual matrix. Off by default; flipping
     * on lights up the per-cell fan-out + SSIM-against-anchor diff
     * panel inside `<VisualBaselineReview>`.
     */
    matrix: VisualMatrixConfigSchema.default({
      enabled: false,
      defaultCells: [],
      maxCells: 8,
    }),
  })
  .strict();
export type VisualAiConfig = z.infer<typeof VisualAiConfigSchema>;
